/**
 * Список редактируемых текстов бота — /messages.
 *
 * Категории: trial / referral / fallback / buy / common.
 * Каждый текст можно отредактировать на /messages/{slug}.
 */
import { useState, useEffect } from "react";
import { List, useTable } from "@refinedev/antd";
import {
  Table, Tag, Typography, Space, Input, Select, Tooltip,
} from "antd";
import {
  SearchOutlined, EditOutlined,
} from "@ant-design/icons";
import { Link } from "react-router";
import { fmtDateTime } from "../../utils";

interface BotMessage {
  slug: string;
  title: string;
  description: string | null;
  category: string;
  parse_mode: string;
  enabled: boolean;
  version: number;
  updated_at: string | null;
  updated_by: number | null;
  html_length: number;
  placeholders: Array<{ name: string; example: string; description?: string }>;
}

const CATEGORY_META: Record<string, { color: string; label: string }> = {
  trial:    { color: "gold",    label: "🎁 Trial" },
  referral: { color: "purple",  label: "👥 Партнёрка" },
  fallback: { color: "magenta", label: "🔒 Fallback (TG-уведомления)" },
  buy:      { color: "blue",    label: "🛒 Покупка" },
  common:   { color: "default", label: "📦 Общее" },
};

export const BotMessagesList = () => {
  const { tableProps, filters, setFilters } = useTable<BotMessage>({
    resource: "messages",
    syncWithLocation: true,
    pagination: { mode: "server", pageSize: 100 },
    sorters: { initial: [] },
  });

  const qF = filters.find(f => "field" in f && f.field === "q");
  const catF = filters.find(f => "field" in f && f.field === "category");
  const [qInput, setQInput] = useState<string>(
    (qF && "value" in qF ? String(qF.value) : "") || ""
  );
  useEffect(() => {
    const t = setTimeout(() => {
      const cleaned = qInput.trim();
      const curr = (qF && "value" in qF ? String(qF.value) : "") || "";
      if (cleaned === curr) return;
      setFilters([
        ...filters.filter(f => "field" in f && f.field !== "q"),
        ...(cleaned ? [{ field: "q", operator: "eq" as const, value: cleaned }] : []),
      ], "replace");
    }, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  return (
    <List title="Тексты бота" breadcrumb={false}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Тексты, которые бот отправляет пользователям. Каждый можно отредактировать —
        изменения видны юзерам в течение минуты (TTL-cache).
        Поддерживается HTML (теги <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>, <code>&lt;code&gt;</code>, <code>&lt;a&gt;</code> и др.)
        и плейсхолдеры в формате <code>{"{name}"}</code>.
      </Typography.Paragraph>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          placeholder="Поиск: slug, название, описание…"
          value={qInput}
          onChange={e => setQInput(e.target.value)}
          style={{ width: 320 }}
        />
        <Select
          style={{ width: 230 }} placeholder="Все категории" allowClear
          value={catF && "value" in catF ? String(catF.value) : undefined}
          onChange={v => setFilters([
            ...filters.filter(f => "field" in f && f.field !== "category"),
            ...(v ? [{ field: "category", operator: "eq" as const, value: v }] : []),
          ], "replace")}
          options={Object.entries(CATEGORY_META).map(([k, m]) => ({
            value: k, label: m.label,
          }))}
        />
      </Space>

      <Table<BotMessage> {...tableProps} rowKey="slug" size="small">
        <Table.Column<BotMessage> dataIndex="title" title="Название"
          render={(v: string, r) => (
            <Link to={`/messages/${r.slug}`}>
              <Typography.Text strong>{v}</Typography.Text>
            </Link>
          )} />
        <Table.Column<BotMessage> dataIndex="slug" title="Slug" width={250}
          render={(v: string) => (
            <Typography.Text code style={{ fontSize: 11 }}>{v}</Typography.Text>
          )} />
        <Table.Column<BotMessage> dataIndex="category" title="Категория" width={180}
          render={(v: string) => {
            const m = CATEGORY_META[v] || { color: "default", label: v };
            return <Tag color={m.color}>{m.label}</Tag>;
          }} />
        <Table.Column<BotMessage> title="Описание"
          render={(_, r) =>
            r.description
              ? <Typography.Text type="secondary" style={{ fontSize: 12 }}
                  ellipsis={{ tooltip: r.description }}>
                  {r.description}
                </Typography.Text>
              : <Typography.Text type="secondary">—</Typography.Text>
          } />
        <Table.Column<BotMessage> dataIndex="placeholders" title="Переменные" width={150}
          render={(v: any[]) => v && v.length
            ? <Tag>{v.length}</Tag>
            : <Typography.Text type="secondary">—</Typography.Text>
          } />
        <Table.Column<BotMessage> dataIndex="version" title="Версия" width={80}
          render={(v: number) => <Tag>v{v}</Tag>} />
        <Table.Column<BotMessage> dataIndex="updated_at" title="Изменён" width={150}
          render={(v: string | null) =>
            v
              ? <Typography.Text style={{ fontSize: 12 }}>{fmtDateTime(v)}</Typography.Text>
              : <Typography.Text type="secondary">—</Typography.Text>
          } />
        <Table.Column<BotMessage> dataIndex="enabled" title="" width={90}
          render={(v: boolean) =>
            v
              ? <Tag color="green">Активен</Tag>
              : <Tag color="default">Отключён</Tag>
          } />
        <Table.Column<BotMessage> width={60}
          render={(_, r) => (
            <Link to={`/messages/${r.slug}`}>
              <Tooltip title="Редактировать">
                <EditOutlined style={{ fontSize: 16, color: "#1677ff" }} />
              </Tooltip>
            </Link>
          )} />
      </Table>
    </List>
  );
};
